import React, { useState, useEffect } from 'react';
import { appointmentApi, patientApi } from '../lib/api';
import api from '../lib/api';
import { createAppointmentResilient } from '../lib/offlineQueue';
import OfflineQueuePanel from '../components/OfflineQueuePanel';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon, Clock, User, Video, Plus,
  ChevronLeft, ChevronRight, Loader2, Copy, Check
} from 'lucide-react';

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patient_id: '', provider_id: '', scheduled_date: '', scheduled_time: '',
    duration_minutes: 30, appointment_type: 'in_person', reason: '', notes: ''
  });

  useEffect(() => { loadAppointments(); }, [selectedDate]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await appointmentApi.list({ date: dateStr });
      setAppointments(response.data.appointments || []);
    } catch (error) { console.error('Failed to load appointments:', error); }
    finally { setLoading(false); }
  };

  const openCreate = async () => {
    setForm(prev => ({ ...prev, scheduled_date: selectedDate.toISOString().split('T')[0] }));
    setShowCreate(true);
    // Load patients — independent of providers, so scheduler works even if /users 403s for nurses/physicians
    try {
      const pRes = await patientApi.list({ limit: 500 });
      setPatients(Array.isArray(pRes.data) ? pRes.data : pRes.data.patients || []);
    } catch (err) {
      console.error('Failed to load patients for scheduler:', err);
      toast.error('Unable to load patient list');
    }
    // Load providers (admins-only endpoint; skip silently for clinical roles)
    try {
      const uRes = await api.get('/users');
      const users = uRes.data.users || uRes.data || [];
      setProviders(users.filter(u => u.role === 'physician' || u.role === 'nurse'));
    } catch {
      setProviders([]);
    }
  };

  const handleCreate = async () => {
    if (!form.patient_id || !form.scheduled_date || !form.scheduled_time) {
      toast.error('Patient, date, and time are required'); return;
    }
    setSaving(true);
    try {
      const res = await createAppointmentResilient(api, form);
      if (!res?.data?.offline) {
        // Emphasise the auto-provisioned video room when telehealth was selected
        if (form.appointment_type === 'telehealth' && res?.data?.telehealth_join_url) {
          toast.success('Telehealth appointment scheduled — video room ready', {
            description: 'A meeting link was created and emailed to both patient and provider.',
            action: {
              label: 'Open video',
              onClick: () => window.open(res.data.telehealth_join_url, '_blank'),
            },
            duration: 8000,
          });
        } else {
          toast.success('Appointment scheduled');
        }
      }
      setShowCreate(false);
      setForm({ patient_id: '', provider_id: '', scheduled_date: '', scheduled_time: '', duration_minutes: 30, appointment_type: 'in_person', reason: '', notes: '' });
      loadAppointments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const copyJoinLink = async (joinUrl) => {
    try {
      const fullUrl = `${window.location.origin}${joinUrl}`;
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Join link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-[#1A56DB]/10 text-[#1A56DB]';
      case 'completed': return 'bg-[#166534]/10 text-[#166534]';
      case 'cancelled': return 'bg-[#991B1B]/10 text-[#991B1B]';
      default: return 'bg-[#92400E]/10 text-[#92400E]';
    }
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <OfflineQueuePanel title="Pending Sync" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Appointments</h1>
          <p className="text-[#64748B] mt-1">Manage your schedule and patient appointments</p>
        </div>
        <Button className="careiq-btn-primary flex items-center gap-2" onClick={openCreate} data-testid="new-appointment-btn">
          <Plus className="w-4 h-4" />New Appointment
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="careiq-card">
          <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342]">Calendar</CardTitle></CardHeader>
          <CardContent>
            <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} className="rounded-md" />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 careiq-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)} className="p-2"><ChevronLeft className="w-4 h-4" /></Button>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigateDate(1)} className="p-2"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())} className="careiq-btn-outline">Today</Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
            ) : appointments.length > 0 ? (
              <div className="space-y-3">
                {appointments.map((appt) => (
                  <div key={appt.id} className="flex items-center gap-4 p-4 rounded-xl border border-[#E2E8F0] hover:shadow-md transition-all" data-testid={`appt-row-${appt.id}`}>
                    <div className="text-center min-w-[80px]">
                      <div className="text-lg font-semibold text-[#0A2342]">{appt.scheduled_time}</div>
                      <div className="text-xs text-[#64748B]">{appt.duration_minutes} min</div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#0E7490]/10 flex items-center justify-center">
                      {appt.appointment_type === 'telehealth' ? <Video className="w-4 h-4 text-[#1A56DB]" /> : <User className="w-4 h-4 text-[#0E7490]" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[#0A2342]">{appt.patient_name}</div>
                      <div className="text-sm text-[#64748B]">{appt.reason || appt.appointment_type?.replace('_', ' ')}</div>
                      {appt.appointment_type === 'telehealth' && appt.telehealth_status === 'mock' && (
                        <div className="text-[10px] text-[#92400E] mt-0.5">Video session ready (mock — Twilio not configured)</div>
                      )}
                    </div>
                    {appt.appointment_type === 'telehealth' && appt.telehealth_join_url && (
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyJoinLink(appt.telehealth_join_url)}
                          className="border-[#0E7490] text-[#0E7490] h-8 px-2"
                          title="Copy join link"
                          data-testid={`copy-join-link-${appt.id}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => window.open(appt.telehealth_join_url, '_blank')}
                          className="bg-[#1A56DB] text-white hover:bg-[#1E40AF] h-8"
                          data-testid={`join-video-${appt.id}`}
                        >
                          <Video className="w-3.5 h-3.5 mr-1" /> Join
                        </Button>
                      </div>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(appt.status)}`}>{appt.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#64748B]">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
                <p className="mb-4">No appointments for this date</p>
                <Button className="careiq-btn-secondary" onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Schedule Appointment</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Appointment Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg" data-testid="create-appointment-dialog">
          <DialogHeader><DialogTitle>Schedule Appointment</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Patient *</Label>
              <Select value={form.patient_id} onValueChange={v => setForm({ ...form, patient_id: v })}>
                <SelectTrigger data-testid="appt-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>{patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.mrn})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={form.provider_id} onValueChange={v => setForm({ ...form, provider_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date *</Label><Input type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} data-testid="appt-date" /></div>
              <div><Label>Time *</Label><Input type="time" value={form.scheduled_time} onChange={e => setForm({ ...form, scheduled_time: e.target.value })} data-testid="appt-time" /></div>
              <div><Label>Duration</Label>
                <Select value={String(form.duration_minutes)} onValueChange={v => setForm({ ...form, duration_minutes: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 min</SelectItem><SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem><SelectItem value="60">60 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.appointment_type} onValueChange={v => setForm({ ...form, appointment_type: v })}>
                <SelectTrigger data-testid="appt-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="telehealth">Telehealth</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                </SelectContent>
              </Select>
              {form.appointment_type === 'telehealth' && (
                <div className="mt-2 flex items-start gap-2 p-2.5 rounded-md bg-[#EFF6FF] border border-[#1A56DB]/20" data-testid="telehealth-hint">
                  <Video className="w-4 h-4 text-[#1A56DB] mt-0.5 shrink-0" />
                  <div className="text-xs text-[#1E40AF]">
                    A secure video session will be <strong>created automatically</strong> when you save. The join link will be emailed to both patient and provider.
                  </div>
                </div>
              )}
            </div>
            <div><Label>Reason</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Reason for visit" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes" className="min-h-[60px]" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleCreate} disabled={saving} data-testid="save-appointment-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-2" />}Schedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
